import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { BookOpen } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { authApi } from '@/api/auth'
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton'
import { GuestAuthShell } from '@/components/auth/GuestAuthShell'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useMinCopy } from '@/i18n/useUiLocale'
import { useAuthStore } from '@/store/authStore'

type RegisterFormValues = {
  displayName?: string
  email: string
  password: string
  confirmPassword: string
}

export function RegisterPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const t = useMinCopy({ preferGuest: true }).auth

  const schema = useMemo(
    () =>
      z
        .object({
          displayName: z.string().max(100).optional(),
          email: z.string().email(t.invalidEmail),
          password: z
            .string()
            .min(8, t.passwordMin)
            .max(128)
            .regex(/[A-Z]/, t.passwordUpper)
            .regex(/[0-9]/, t.passwordDigit),
          confirmPassword: z.string(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t.passwordsMismatch,
          path: ['confirmPassword'],
        }),
    [t],
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: RegisterFormValues) => {
    clearErrors('root')

    try {
      const registerResponse = await authApi.register({
        email: data.email,
        password: data.password,
        displayName: data.displayName?.trim() || undefined,
      })
      if (!registerResponse.data.verificationRequired && registerResponse.data.accessToken) {
        const profileResponse = await authApi.getProfile(registerResponse.data.accessToken)
        setAuth(registerResponse.data.accessToken, profileResponse.data)
        navigate('/dashboard', { replace: true })
        return
      }
      navigate('/verify-email', {
        replace: true,
        state: { email: registerResponse.data.email },
      })
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        setError('root', { message: t.unableRegister })
        return
      }

      if (error.response?.status === 409) {
        setError('email', { message: t.emailAlreadyRegistered })
        return
      }

      setError('root', { message: t.unableRegister })
    }
  }

  return (
    <GuestAuthShell>
      <Card className="relative w-full max-w-md border-white/60 bg-white/90 shadow-2xl backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <BookOpen className="h-6 w-6" />
          </div>
          <CardTitle>{t.registerTitle}</CardTitle>
          <CardDescription>{t.registerDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="displayName">{t.displayName}</Label>
              <Input id="displayName" type="text" autoComplete="name" {...register('displayName')} />
              {errors.displayName ? (
                <p className="text-sm text-destructive">{errors.displayName.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                hasError={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email ? (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                hasError={Boolean(errors.password)}
                {...register('password')}
              />
              {errors.password ? (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t.confirmPassword}</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                hasError={Boolean(errors.confirmPassword)}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              ) : null}
            </div>

            {errors.root?.message ? (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            ) : null}

            <Button type="submit" loading={isSubmitting} data-testid="register-submit">
              {t.createAccount}
            </Button>

            <p className="text-center text-xs leading-relaxed text-muted-foreground">
              {t.agreePrefix}{' '}
              <Link className="font-medium text-primary hover:underline" to="/terms">
                {t.termsOfService}
              </Link>{' '}
              {t.agreeAnd}{' '}
              <Link className="font-medium text-primary hover:underline" to="/privacy">
                {t.privacyPolicy}
              </Link>
              {t.agreeSuffix}
            </p>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{t.or}</span>
              </div>
            </div>

            <GoogleSignInButton returnUrl="/dashboard" label={t.continueWithGoogle} />

            <p className="text-center text-sm text-muted-foreground">
              {t.haveAccount}{' '}
              <Link className="font-medium text-primary hover:underline" to="/login">
                {t.signInLink}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </GuestAuthShell>
  )
}
